import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import { cn } from '@/lib/utils';

interface MarkdownRendererProps {
  content: string;
  onWikilinkClick?: (target: string) => void;
  className?: string;
}

// Schema estendido: permite data-wikilink em <a>
const schema = {
  ...defaultSchema,
  attributes: {
    ...defaultSchema.attributes,
    a: [...(defaultSchema.attributes?.a ?? []), ['data-wikilink']],
  },
};

/**
 * Pre-processa wikilinks [[Alvo]] e [[Alvo|alias]] convertendo pra
 * links markdown especiais que o ReactMarkdown consegue renderizar.
 * Usa uma classe custom + data-attribute pra capturarmos o click.
 */
function preprocessWikilinks(md: string): string {
  return md.replace(/\[\[([^\]|#]+?)(?:#[^\]|]*)?(?:\|([^\]]+))?\]\]/g, (_m, target, alias) => {
    const text = (alias ?? target) as string;
    const url = `#wikilink:${encodeURIComponent(target)}`;
    return `[${text}](${url})`;
  });
}

/**
 * Renderiza callouts `> [!note] Título` estilo Obsidian.
 * Pré-processa pra virar blockquote com classe data-callout.
 */
function preprocessCallouts(md: string): string {
  return md.replace(
    /^> \[!(\w+)\]\s*([^\n]*)?/gm,
    (_m, kind: string, title: string) =>
      `> **${(title || kind).trim()}** {.callout-${kind.toLowerCase()}}`,
  );
}

export function MarkdownRenderer({
  content,
  onWikilinkClick,
  className,
}: MarkdownRendererProps): JSX.Element {
  const processed = preprocessCallouts(preprocessWikilinks(content));

  return (
    <div
      className={cn(
        'prose prose-invert prose-sm max-w-none',
        'prose-headings:font-semibold prose-headings:text-foreground',
        'prose-p:text-foreground prose-li:text-foreground',
        'prose-strong:text-foreground prose-em:text-foreground',
        'prose-code:text-foreground prose-code:bg-muted prose-code:rounded prose-code:px-1 prose-code:py-0.5 prose-code:before:content-none prose-code:after:content-none',
        'prose-pre:bg-muted prose-pre:border prose-pre:border-border prose-pre:rounded-md',
        'prose-blockquote:border-primary/40 prose-blockquote:text-muted-foreground',
        'prose-a:text-primary prose-a:no-underline hover:prose-a:underline',
        'prose-hr:border-border',
        'prose-table:text-sm prose-table:border-collapse',
        'prose-th:border prose-th:border-border prose-th:px-2 prose-th:py-1 prose-th:bg-muted',
        'prose-td:border prose-td:border-border prose-td:px-2 prose-td:py-1',
        className,
      )}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[[rehypeSanitize, schema]]}
        components={{
          a({ href, children, ...props }) {
            if (href?.startsWith('#wikilink:')) {
              const target = decodeURIComponent(href.slice('#wikilink:'.length));
              return (
                <button
                  type="button"
                  onClick={() => onWikilinkClick?.(target)}
                  className="text-primary hover:underline font-medium inline"
                >
                  {children}
                </button>
              );
            }
            if (href?.startsWith('http')) {
              return (
                <a href={href} target="_blank" rel="noreferrer" {...props}>
                  {children}
                </a>
              );
            }
            return <a href={href} {...props}>{children}</a>;
          },
        }}
      >
        {processed}
      </ReactMarkdown>
    </div>
  );
}
